import { JsonPipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { FormlyFieldConfig, FormlyForm } from '@ngx-formly/core';
import {
  AutoSaveFormBase,
  FormGroupControlsOf,
} from '../base-classes/base-form.class';
import {
  ISurveyPlugin,
  SurveyPluginUsage,
} from '@hyper-inferno/survey-elements-core';

@Component({
  selector: 'app-plugin-usage-detail',
  imports: [ReactiveFormsModule, FormlyForm, JsonPipe],
  templateUrl: './plugin-usage-detail.html',
  styleUrl: './plugin-usage-detail.css',
})
export class PluginUsageDetail extends AutoSaveFormBase<SurveyPluginUsage> {
  override entityName = 'survey_plugin_usage';
  entityId = input.required<string>();
  surveyPlugins = input.required<ISurveyPlugin[]>();

  // Manual-save state
  isSaving = signal(false);
  saveError = signal<string | null>(null);

  override form = this.fb.group<FormGroupControlsOf<SurveyPluginUsage>>({
    id: this.fb.control<any | undefined>(undefined),
    plugin_id: this.fb.control<any | undefined>(undefined),
    element_id: this.fb.control<any | undefined>(undefined),
    page_id: this.fb.control<any | undefined>(undefined),
    survey_id: this.fb.control<any | undefined>(undefined),
    plugin_values: this.fb.group({}) as any,
    plugin_type: this.fb.control<any | undefined>(undefined),
  });

  get pluginValuesGroup(): FormGroup {
    return this.form.get('plugin_values') as FormGroup;
  }

  model = { plugin_values: {} as Record<string, any> };

  currentIdPluginId = signal<string | undefined>(undefined);
  isSchemaReady = signal(false);

  private lastEntityId?: string;

  dynamicFields = computed<FormlyFieldConfig[]>(() => {
    const pluginId = this.currentIdPluginId();
    if (!pluginId) return [];

    const plugin = this.surveyPlugins().find((p) => p.id === pluginId);
    if (!plugin?.config_schema) return [];

    try {
      return typeof plugin.config_schema === 'string'
        ? JSON.parse(plugin.config_schema)
        : (plugin.config_schema as FormlyFieldConfig[]);
    } catch (error) {
      console.error('Failed to parse config_schema:', error);
      return [];
    }
  });

  constructor() {
    super();

    // Reset local state when switching usage records.
    effect(() => {
      const id = this.entityId();
      if (!id || id === this.lastEntityId) return;

      this.lastEntityId = id;
      this._hydration_done = false;
      this.isSchemaReady.set(false);
      this.currentIdPluginId.set(undefined);
      this.saveError.set(null);
      this.model = { plugin_values: {} };

      Object.keys(this.pluginValuesGroup.controls).forEach((key) => {
        this.pluginValuesGroup.removeControl(key, {
          emitEvent: false,
        });
      });

      this.form.reset(
        {
          id,
          plugin_id: undefined,
          element_id: undefined,
          page_id: undefined,
          survey_id: undefined,
          plugin_values: {},
          plugin_type: undefined,
        },
        { emitEvent: false },
      );
    });

    // The schema is ready once the selected plugin has fields.
    effect(() => {
      this.isSchemaReady.set(
        this.dynamicFields().length > 0 && !!this.currentIdPluginId(),
      );
    });

    this.form.get('plugin_id')?.valueChanges.subscribe((value) => {
      this.currentIdPluginId.set(value ?? undefined);
    });
    this.form.valueChanges.subscribe(() => {
      if (this._hydration_done) {
        this.configurationDirtyChange.emit(this.form.dirty);
      }
    });
  }

  // Disable autosave and the base class's global-save listener.
  // Do NOT call super.ngOnInit().
  override ngOnInit(): void {}

  // Reject hydration results belonging to a previously selected usage.
  protected override hydrate(data: SurveyPluginUsage): void {
    if (!data || data.id !== this.entityId()) return;

    this._hydration_done = false;
    this.rebuildFormArrays(data);

    this.form.patchValue(
      {
        id: data.id,
        plugin_id: data.plugin_id,
        element_id: data.element_id,
        page_id: data.page_id,
        survey_id: data.survey_id,
        plugin_type: data.plugin_type,
      },
      { emitEvent: false },
    );

    this.form.markAsPristine();
    this.postHydrate();
    this._hydration_done = true;
  }

  protected override rebuildFormArrays(data: any): void {
    if (!data) return;

    let savedValues: Record<string, any> = {};

    try {
      savedValues =
        typeof data.plugin_values === 'string'
          ? JSON.parse(data.plugin_values || '{}')
          : { ...(data.plugin_values ?? {}) };
    } catch (error) {
      console.error('Failed to parse plugin_values:', error);
    }

    const plugin = this.surveyPlugins().find((p) => p.id === data.plugin_id);

    let fields: FormlyFieldConfig[] = [];

    try {
      fields = plugin?.config_schema
        ? typeof plugin.config_schema === 'string'
          ? JSON.parse(plugin.config_schema)
          : (plugin.config_schema as FormlyFieldConfig[])
        : [];
    } catch (error) {
      console.error('Failed to parse config_schema:', error);
    }

    // Only hydrate keys defined by this plugin's schema.
    const allowedKeys = new Set(
      fields
        .map((field) => field.key)
        .filter((key): key is string => typeof key === 'string'),
    );

    const filteredValues = Object.fromEntries(
      Object.entries(savedValues).filter(([key]) => allowedKeys.has(key)),
    );

    this.currentIdPluginId.set(data.plugin_id ?? undefined);
    this.model = {
      plugin_values: structuredClone(filteredValues),
    };

    this.pluginValuesGroup.reset(filteredValues, {
      emitEvent: false,
    });
  }

  async savePluginUsage(): Promise<boolean> {
    if (this.isSaving() || !this._hydration_done || !this.form.valid) {
      return false;
    }

    // Nothing to save is a successful no-op.
    if (this.form.pristine) {
      return true;
    }

    const id = this.entityId();

    if (!id || this.form.get('id')?.value !== id) {
      return false;
    }

    const pluginValues = structuredClone(
      this.pluginValuesGroup.getRawValue() ?? {},
    );

    this.isSaving.set(true);
    this.saveError.set(null);

    try {
      const { error } = await this.supabase.client
        .from(this.entityName)
        .update({ plugin_values: pluginValues })
        .eq('id', id);

      if (error) throw error;

      if (this.entityId() === id) {
        this.form.markAsPristine();
        this.lastSaved = new Date();
      }

      this.eventsService.notifySave();

      return true;
    } catch (error: any) {
      console.error('Failed to save plugin configuration:', error);

      this.saveError.set(
        error?.message ?? 'Failed to save plugin configuration.',
      );

      return false;
    } finally {
      this.isSaving.set(false);
    }
  }

  override getFormValidationErrors(): string[] {
    const messages: string[] = [];

    const extractErrors = (control: any, path = '') => {
      if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach((key) => {
          extractErrors(control.get(key), path ? `${path} -> ${key}` : key);
        });
      } else if (control instanceof FormArray) {
        control.controls.forEach((child, index) => {
          extractErrors(child, `${path}[${index}]`);
        });
      } else if (control?.errors) {
        Object.keys(control.errors).forEach((key) => {
          messages.push(`Field "${path}" is invalid: ${key}`);
        });
      }
    };

    extractErrors(this.form);
    return messages;
  }
  configurationDirtyChange = output<boolean>();
  onPluginModelChange(values: Record<string, any>) {
    this.model = {
      ...this.model,
      plugin_values: values,
    };

    if (this._hydration_done) {
      this.configurationDirtyChange.emit(
        this.pluginValuesGroup.dirty || this.form.dirty,
      );
    }
  }
}
